/**
 * Forum d'une poule en compétition (fft.c_forums).
 *
 * Identifiant `forum` dans la base (bucket passé à l’API division) :
 * id division affiché + (id phase − 1) × 10000 — utilisé par le legacy PHP pour
 * `championnat_indiv.php` (CIA) et `championnat_eq.php` (championnat par équipes).
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { PlayerAvatar } from '../PlayerAvatar';
import { BackArrowIcon } from '../Icons';
import ClickablePseudo from '../ClickablePseudo';
import { ForumEmoticonPicker } from '../ForumEmoticonPicker';
import { ForumThreadItem, type ForumThreadListItem } from '../forums/ForumThreadItem';
import { useResponsive } from '../../hooks/useResponsive';
import { BACKEND_URL } from '../../config';
import '../../styles/forums.css';
import { processForumContent } from '../../utils/forumContent';
import { formatFullDate } from '../../utils/forumDates';
import { pillStyleFromString } from '../../utils/stringColor';
import { convertEmoticonsToEmojis, convertForumEmojisToSmileys, insertAtCursor } from '../../utils/emoticons';
import {
  clearForumReplyDraft,
  divisionForumReplyDraftScope,
  forumReplyDraftKey,
  loadForumReplyDraft,
  saveForumReplyDraft,
} from '../../utils/forumReplyDrafts';

export function competitionDivisionForumBucketId(divisionId: number, phaseId: number): number {
  if (divisionId <= 0) return divisionId;
  return divisionId + (phaseId - 1) * 10000;
}

/** Forum phase « présentation générale » : fid = -(100000 + idphase) — CIA (`championnat_indiv.php`) et championnat équipes (`championnat_eq.php`). */
export function ciaPhaseGeneralForumBucketId(phaseId: number): number {
  return -(100000 + phaseId);
}

type ForumThread = ForumThreadListItem;

interface ForumMessage {
  id: number;
  author: string;
  authorDisplay: string;
  subject: string;
  body: string;
  date: string;
}

export interface DivisionForumPanelProps {
  /** Non requis si `teamId` est fourni (forum d'équipe plutôt que forum de poule). */
  divisionId?: number;
  phaseId?: number;
  /**
   * Forum d'une équipe (fft.forums_equipe) plutôt qu'une poule de championnat :
   * quand renseigné, `divisionId`/`phaseId`/`forumBucketOverride` sont ignorés.
   */
  teamId?: number;
  /**
   * Championnat par équipes : pseudo (minuscule) → nom d’équipe pour la composition
   * de la poule sur la phase courante (ex. dérivé de `/team-champ/.../teams`).
   * Ne pas passer pour le CIA ou si les données ne sont pas disponibles.
   */
  authorTeamNameLookup?: ReadonlyMap<string, string>;
  /**
   * Si renseigné, identifiant `forum` dans fft.c_forums (remplace le calcul division + phase).
   * Ex. forum phase CIA : {@link ciaPhaseGeneralForumBucketId}.
   */
  forumBucketOverride?: number;
  /** Pseudo connecté (affichage + en-tête X-User-Pseudo pour publication). */
  viewerPseudo: string;
  /** Si false, consultation seule (pas de nouveau sujet / réponse). */
  allowCompose?: boolean;
  /**
   * false si l’utilisateur n’est pas un compétiteur de cette poule (autre division ou pas inscrit).
   * défaut true (comportement neutre si non renseigné).
   */
  canPost?: boolean;
  /** Décrémente le compteur hub quand un sujet est marqué lu. */
  onUnreadCleared?: (clearedCount: number) => void;
}

function normPseudoKey(p: string): string {
  return p.trim().toLowerCase();
}

/**
 * L'API renvoie parfois `{ error: { code, message, timestamp } }` (erreur
 * structurée) plutôt que `{ error: "message" }` : on extrait toujours une
 * chaîne lisible pour éviter d'afficher "[object Object]" à l'utilisateur.
 */
function extractErrorMessage(json: unknown, fallback: string): string {
  const err = (json as { error?: unknown } | null)?.error;
  if (typeof err === 'string' && err) return err;
  if (err && typeof err === 'object' && typeof (err as { message?: unknown }).message === 'string') {
    return (err as { message: string }).message;
  }
  return fallback;
}

function TeamNamePill({ name }: { name: string }) {
  return (
    <span className="forums-team-name-pill" style={pillStyleFromString(name)} title={name}>
      {name}
    </span>
  );
}

export function DivisionForumPanel({
  divisionId,
  phaseId,
  teamId,
  forumBucketOverride,
  viewerPseudo,
  authorTeamNameLookup,
  allowCompose = true,
  canPost,
  onUnreadCleared,
}: DivisionForumPanelProps) {
  const { isCompact } = useResponsive();

  const mayPost =
    (canPost ?? true) &&
    allowCompose !== false &&
    Boolean(String(viewerPseudo || '').trim());
  const [threads, setThreads] = useState<ForumThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [selectedThreadId, setSelectedThreadId] = useState<number | null>(null);
  const [messages, setMessages] = useState<ForumMessage[]>([]);
  const [loadingMsg, setLoadingMsg] = useState(false);

  const [showNewThread, setShowNewThread] = useState(false);
  const [newSubject, setNewSubject] = useState('');
  const [newBody, setNewBody] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [posting, setPosting] = useState(false);
  const [composeErr, setComposeErr] = useState<string | null>(null);
  const [showReplyEmoticons, setShowReplyEmoticons] = useState(false);
  const [showNewEmoticons, setShowNewEmoticons] = useState(false);
  const replyBodyRef = useRef<HTMLTextAreaElement>(null);
  const newBodyRef = useRef<HTMLTextAreaElement>(null);
  const replyDraftKeyRef = useRef<string | null>(null);
  const replyBodyDraftRef = useRef('');
  replyBodyDraftRef.current = replyBody;
  const onUnreadClearedRef = useRef(onUnreadCleared);
  onUnreadClearedRef.current = onUnreadCleared;

  const forumBucketId = useMemo(
    () =>
      forumBucketOverride !== undefined
        ? forumBucketOverride
        : competitionDivisionForumBucketId(divisionId || 0, phaseId || 0),
    [forumBucketOverride, divisionId, phaseId],
  );

  const forumDraftScope = useMemo(
    () => divisionForumReplyDraftScope({ teamId, forumBucketId }),
    [teamId, forumBucketId],
  );

  const buildReplyDraftKey = useCallback((threadId: number | null): string | null => {
    if (threadId == null || !viewerPseudo.trim()) return null;
    return forumReplyDraftKey('competition', viewerPseudo, forumDraftScope, threadId);
  }, [viewerPseudo, forumDraftScope]);

  const persistCurrentReplyDraft = useCallback(() => {
    const key = replyDraftKeyRef.current;
    if (!key) return;
    const body = replyBodyDraftRef.current;
    if (body.trim()) saveForumReplyDraft(key, body);
    else clearForumReplyDraft(key);
  }, []);

  const switchReplyDraft = useCallback((threadId: number | null) => {
    persistCurrentReplyDraft();
    const nextKey = buildReplyDraftKey(threadId);
    replyDraftKeyRef.current = nextKey;
    const loaded = nextKey ? loadForumReplyDraft(nextKey) : '';
    replyBodyDraftRef.current = loaded;
    setReplyBody(loaded);
    setShowReplyEmoticons(false);
  }, [persistCurrentReplyDraft, buildReplyDraftKey]);

  const clearReplyDraftState = useCallback(() => {
    persistCurrentReplyDraft();
    replyDraftKeyRef.current = null;
    replyBodyDraftRef.current = '';
    setReplyBody('');
    setShowReplyEmoticons(false);
  }, [persistCurrentReplyDraft]);

  useEffect(() => {
    const key = replyDraftKeyRef.current;
    if (!key) return;
    const timer = window.setTimeout(() => {
      const body = replyBodyDraftRef.current;
      if (body.trim()) saveForumReplyDraft(key, body);
      else clearForumReplyDraft(key);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [replyBody]);

  const apiBase = teamId != null
    ? `${BACKEND_URL}/api/forums/team/${teamId}`
    : `${BACKEND_URL}/api/forums/division/${forumBucketId}`;

  const selectedThread = useMemo(() => {
    if (selectedThreadId == null) return null;
    return threads.find((t) => (t.thread || t.id) === selectedThreadId) ?? null;
  }, [threads, selectedThreadId]);

  // Sticky en tête (comme page Forums)
  const sortedThreads = useMemo(
    () =>
      [...threads].sort((a, b) => {
        if (Boolean(a.sticky) !== Boolean(b.sticky)) return a.sticky ? -1 : 1;
        const da = a.lastReply || a.date;
        const db = b.lastReply || b.date;
        return db.localeCompare(da);
      }),
    [threads],
  );

  const closeThreadView = useCallback(() => {
    clearReplyDraftState();
    setSelectedThreadId(null);
    setMessages([]);
  }, [clearReplyDraftState]);

  const teamNameForPseudo = useCallback(
    (pseudo: string) => authorTeamNameLookup?.get(normPseudoKey(pseudo)),
    [authorTeamNameLookup],
  );

  const loadThreads = useCallback(async () => {
    const res = await fetch(`${apiBase}/threads?limit=40&offset=0`, {
      headers: viewerPseudo ? { 'X-User-Pseudo': viewerPseudo } : undefined,
    });
    const json = await res.json();
    if (!res.ok) throw new Error(extractErrorMessage(json, 'forum_load_failed'));
    setThreads((json as { threads?: ForumThread[] }).threads || []);
  }, [apiBase, viewerPseudo]);

  useEffect(() => {
    clearReplyDraftState();
    setSelectedThreadId(null);
    setMessages([]);
    setComposeErr(null);
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        await loadThreads();
      } catch (e) {
        setErr(e instanceof Error ? e.message : 'Erreur forum');
      } finally {
        setLoading(false);
      }
    })();
  }, [loadThreads, clearReplyDraftState]);

  const loadMessagesFor = useCallback(
    async (t: ForumThread) => {
      const tid = t.thread || t.id;
      setLoadingMsg(true);
      try {
        const res = await fetch(`${apiBase}/threads/${tid}`, {
          headers: viewerPseudo ? { 'X-User-Pseudo': viewerPseudo } : undefined,
        });
        const json = await res.json();
        if (res.ok) setMessages((json as { messages?: ForumMessage[] }).messages || []);
        else setMessages([]);
      } catch {
        setMessages([]);
      } finally {
        setLoadingMsg(false);
      }
    },
    [apiBase, viewerPseudo],
  );

  const openThread = useCallback(
    async (t: ForumThread) => {
      const tid = t.thread || t.id;
      setSelectedThreadId(tid);
      switchReplyDraft(tid);
      // Ouverture → API marque lu ; maj locale pour la bulle hub
      if (t.unread === true) {
        const cleared =
          typeof t.unreadCount === 'number' && t.unreadCount > 0 ? t.unreadCount : 1;
        setThreads((prev) =>
          prev.map((x) =>
            (x.thread || x.id) === tid ? { ...x, unread: false, unreadCount: 0 } : x,
          ),
        );
        onUnreadClearedRef.current?.(cleared);
      }
      await loadMessagesFor(t);
    },
    [loadMessagesFor, switchReplyDraft],
  );

  const closeNewThreadModal = useCallback(() => {
    setShowNewThread(false);
    setNewSubject('');
    setNewBody('');
    setShowNewEmoticons(false);
    setComposeErr(null);
  }, []);

  const createThread = useCallback(async () => {
    if (!mayPost || !newSubject.trim() || !newBody.trim()) return;
    setPosting(true);
    setComposeErr(null);
    try {
      const res = await fetch(`${apiBase}/threads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Pseudo': viewerPseudo },
        body: JSON.stringify({
          subject: newSubject.trim(),
          body: convertForumEmojisToSmileys(newBody.trim()),
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setComposeErr(extractErrorMessage(json, `Erreur ${res.status}`));
        return;
      }
      const newId = (json as { id?: number }).id;
      const subjectSnap = newSubject.trim();
      closeNewThreadModal();
      await loadThreads();
      if (typeof newId === 'number') {
        const synthetic: ForumThread = {
          id: newId,
          thread: newId,
          author: viewerPseudo,
          authorDisplay: viewerPseudo,
          subject: subjectSnap,
          date: new Date().toISOString(),
          replyCount: 0,
          lastReply: null,
        };
        await openThread(synthetic);
      }
    } catch {
      setComposeErr('Échec de publication');
    } finally {
      setPosting(false);
    }
  }, [mayPost, newSubject, newBody, apiBase, closeNewThreadModal, loadThreads, openThread, viewerPseudo]);

  const replyToThread = useCallback(async () => {
    if (!mayPost || !selectedThread || !replyBody.trim()) return;
    const threadId = selectedThread.thread || selectedThread.id;
    setPosting(true);
    setComposeErr(null);
    try {
      const res = await fetch(`${apiBase}/threads/${threadId}/replies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Pseudo': viewerPseudo },
        body: JSON.stringify({
          subject: `Re: ${selectedThread.subject}`,
          body: convertForumEmojisToSmileys(replyBody.trim()),
        }),
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        setComposeErr(extractErrorMessage(json, `Erreur ${res.status}`));
        return;
      }
      if (replyDraftKeyRef.current) clearForumReplyDraft(replyDraftKeyRef.current);
      replyBodyDraftRef.current = '';
      setReplyBody('');
      setShowReplyEmoticons(false);
      await loadMessagesFor(selectedThread);
      await loadThreads();
    } catch {
      setComposeErr('Échec de la réponse');
    } finally {
      setPosting(false);
    }
  }, [
    mayPost,
    selectedThread,
    replyBody,
    viewerPseudo,
    apiBase,
    loadMessagesFor,
    loadThreads,
  ]);

  const applyComposeText = useCallback((
    nextRaw: string,
    textarea: HTMLTextAreaElement | null,
    setBody: (v: string) => void,
  ) => {
    const next = convertEmoticonsToEmojis(nextRaw);
    setBody(next);
    if (!textarea || next === nextRaw) return;
    const delta = next.length - nextRaw.length;
    const caret = Math.max(0, (textarea.selectionStart ?? next.length) + delta);
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(caret, caret);
    });
  }, []);

  const insertReplyEmoji = useCallback((emoji: string) => {
    const { next, caret } = insertAtCursor(replyBody, emoji, replyBodyRef.current);
    setReplyBody(next);
    setShowReplyEmoticons(false);
    requestAnimationFrame(() => {
      const ta = replyBodyRef.current;
      if (!ta) return;
      ta.focus();
      ta.setSelectionRange(caret, caret);
    });
  }, [replyBody]);

  const insertNewEmoji = useCallback((emoji: string) => {
    const { next, caret } = insertAtCursor(newBody, emoji, newBodyRef.current);
    setNewBody(next);
    setShowNewEmoticons(false);
    requestAnimationFrame(() => {
      const ta = newBodyRef.current;
      if (!ta) return;
      ta.focus();
      ta.setSelectionRange(caret, caret);
    });
  }, [newBody]);

  if (loading) return <div className="compet-loading">Chargement du forum…</div>;
  if (err) return <div className="compet-error">{err}</div>;

  const threadTitle =
    selectedThread?.subject ?? (messages[0]?.subject ? messages[0].subject : null) ?? 'Discussion';

  const compactHeaderTeamName = selectedThread ? teamNameForPseudo(selectedThread.author) : undefined;

  const replyForm =
    mayPost ? (
      <div className="forums-reply-form cia-forum-reply-form">
        <textarea
          ref={replyBodyRef}
          className="forums-reply-input"
          placeholder="Écrire une réponse…"
          value={replyBody}
          onChange={(e) => applyComposeText(e.target.value, replyBodyRef.current, setReplyBody)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              replyToThread();
            }
          }}
          rows={3}
        />
        <div className="forums-compose-tools">
          <ForumEmoticonPicker
            open={showReplyEmoticons}
            onToggle={() => setShowReplyEmoticons((v) => !v)}
            onSelect={insertReplyEmoji}
          />
        </div>
        <div className="forums-reply-actions">
          <span className="forums-reply-hint">Ctrl+Entrée pour envoyer</span>
          <button
            type="button"
            className="forums-reply-send"
            onClick={replyToThread}
            disabled={posting || !replyBody.trim()}
          >
            {posting ? 'Envoi…' : 'Répondre'}
          </button>
        </div>
      </div>
    ) : null;

  const newThreadModal =
    showNewThread && mayPost ? (
      <div
        className="forums-modal-overlay"
        onClick={(e) => {
          if (e.target === e.currentTarget) closeNewThreadModal();
        }}
        role="presentation"
      >
        <div className="forums-modal">
          <div className="forums-modal-header">
            <h3>Nouveau sujet</h3>
            <button type="button" className="forums-modal-close" onClick={closeNewThreadModal}>
              ×
            </button>
          </div>
          <div className="forums-modal-body">
            {composeErr && <div className="compet-error" style={{ marginBottom: 8 }}>{composeErr}</div>}
            <input
              type="text"
              placeholder="Titre du sujet"
              value={newSubject}
              onChange={(e) => setNewSubject(e.target.value)}
              className="forums-input"
              autoFocus
            />
            <textarea
              ref={newBodyRef}
              placeholder="Votre message…"
              value={newBody}
              onChange={(e) => applyComposeText(e.target.value, newBodyRef.current, setNewBody)}
              className="forums-textarea"
              rows={6}
            />
            <div className="forums-compose-tools">
              <ForumEmoticonPicker
                open={showNewEmoticons}
                onToggle={() => setShowNewEmoticons((v) => !v)}
                onSelect={insertNewEmoji}
              />
            </div>
          </div>
          <div className="forums-modal-footer">
            <button type="button" className="forums-cancel-btn" onClick={closeNewThreadModal}>
              Annuler
            </button>
            <button
              type="button"
              className="forums-submit-btn"
              onClick={createThread}
              disabled={posting || !newSubject.trim() || !newBody.trim()}
            >
              {posting ? 'Envoi…' : 'Publier'}
            </button>
          </div>
        </div>
      </div>
    ) : null;

  const threadList = (
    <>
      {mayPost && (
        <div className="cia-forum-toolbar">
          <button
            type="button"
            className="cia-forum-new-subject-btn"
            onClick={() => {
              setComposeErr(null);
              setShowNewThread(true);
            }}
          >
            Nouveau sujet
          </button>
        </div>
      )}
      <div className="cia-forum-threads-title">Sujets</div>
      {threads.length === 0 ? (
        <div className="compet-empty">Aucun sujet dans ce forum</div>
      ) : (
        sortedThreads.map((t) => {
          const tid = t.thread || t.id;
          return (
            <ForumThreadItem
              key={tid}
              thread={t}
              isActive={selectedThreadId === tid}
              onClick={() => openThread(t)}
            />
          );
        })
      )}
    </>
  );

  const messageItems = messages.map((m, i) => {
    const isOwn = m.author.toLowerCase() === viewerPseudo.toLowerCase();
    const msgTeam = teamNameForPseudo(m.author);
    return (
      <div
        key={m.id}
        className={`forums-message forums-message--stacked${i === 0 ? ' original' : ''}${isOwn ? ' own' : ''}`}
      >
        <div className="forums-message-top">
          <div className="forums-message-sidebar">
            <PlayerAvatar pseudo={m.author} size={36} />
          </div>
          <div className="forums-message-header">
            <div className="forums-message-author-block">
              <span className="forums-message-author">
                <ClickablePseudo pseudo={m.author}>{m.authorDisplay}</ClickablePseudo>
              </span>
              {msgTeam ? <TeamNamePill name={msgTeam} /> : null}
            </div>
            <span className="forums-message-date">{formatFullDate(m.date)}</span>
          </div>
        </div>
        <div className="forums-message-below">
          {m.subject ? <div className="cia-forum-msg-subject">{m.subject}</div> : null}
          <div
            className="forums-message-body"
            dangerouslySetInnerHTML={{ __html: processForumContent(m.body) }}
          />
        </div>
      </div>
    );
  });

  if (isCompact && selectedThreadId != null) {
    return (
      <>
        {newThreadModal}
        <div className="cia-forum-layout cia-forum-layout--compact-messages">
          <div className="forums-panel-full cia-forum-compact-thread-wrap">
            <div className="forums-col-header">
              <button
                type="button"
                className="forums-back-btn"
                onClick={closeThreadView}
                aria-label="Retour aux sujets"
              >
                <BackArrowIcon size={18} />
              </button>
              <div className="forums-col-header-info">
                <h2>{threadTitle}</h2>
                {selectedThread && (
                  <>
                    <span className="forums-col-header-sub">{selectedThread.authorDisplay}</span>
                    {compactHeaderTeamName ? <TeamNamePill name={compactHeaderTeamName} /> : null}
                  </>
                )}
              </div>
            </div>
            {composeErr && selectedThreadId != null && (
              <div className="compet-error" style={{ margin: '0 12px 8px' }}>
                {composeErr}
              </div>
            )}
            {loadingMsg ? (
              <div className="compet-loading cia-forum-compact-loading">Chargement…</div>
            ) : (
              <div className="forums-col-scroll forums-messages-scroll cia-forum-msg-scroll">
                {messageItems}
                {replyForm}
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  if (isCompact) {
    return (
      <>
        {newThreadModal}
        <div className="cia-forum-layout cia-forum-layout--compact-list">
          <div className="cia-forum-threads cia-forum-threads--compact-full">{threadList}</div>
        </div>
      </>
    );
  }

  return (
    <>
      {newThreadModal}
      <div className="cia-forum-layout">
        <div className="cia-forum-threads">{threadList}</div>
        <div className="cia-forum-messages">
          {composeErr && selectedThreadId != null && (
            <div className="compet-error" style={{ marginBottom: 8 }}>{composeErr}</div>
          )}
          {selectedThreadId == null ? (
            <div className="compet-empty cia-forum-messages-empty">Sélectionnez un sujet</div>
          ) : loadingMsg ? (
            <div className="compet-loading">Chargement…</div>
          ) : (
            <>
              <div className="forums-messages-scroll cia-forum-msg-scroll">{messageItems}</div>
              {replyForm}
            </>
          )}
        </div>
      </div>
    </>
  );
}
